/**
 * PrimitiveComponent - base for components with geometry (renderable or collision).
 * Manages physics options and transform sync with physics engine.
 */

import * as THREE from 'three';

import { CollisionMeshType, PhysicsMotionType } from '../physics/PhysicsTypes.js';
import { AbstractEngineClass } from '../systems/ClassRegistry.js';
import { DefaultCollisionProfile } from '../systems/index.js';
import { Delegate } from '../utils/Delegate.js';
import { property } from '../utils/serialization/decorator.js';

import { SceneComponent } from './SceneComponent.js';

import type { SceneComponentOptions } from './SceneComponent.js';
import type { CollisionEvent, IPhysicsEngine, PhysicsScalarParam, PhysicsVectorParam, VectorArray } from '../physics/PhysicsEngine.js';
import type { ComponentPhysicsOptions } from '../physics/PhysicsTypes.js';
import type { EditorPropertyChangedResult } from '../utils/EditorClassMeta.js';

/**
 * Options for configuring a PrimitiveComponent
 */
export interface PrimitiveComponentOptions extends SceneComponentOptions {
  /** Physics configuration options, if not specified, defaults to DEFAULT_PHYSICS_OPTIONS */
  physicsOptions?: ComponentPhysicsOptions;
  /** Controls how this component participates in navmesh generation */
  navigationGeometryRole?: NavigationRole;
  /** When true, this component is excluded from navigation mesh generation */
  excludeFromNavigation?: boolean;
}

export enum NavigationRole {
  Walkable = 'walkable', // contributes floor surfaces as walkable areas
  Obstacle = 'obstacle', // blocks paths but is never walkable
  Exclude = 'exclude', // omits geometry from the bake
}

/**
 * Flags for controlling physics transform updates
 */
export interface PhysicsTransformUpdateFlags {
  sendPosition: boolean;
  sendRotation: boolean;
  receivePosition: boolean;
  receiveRotation: boolean;
}

/**
 * PrimitiveComponents are SceneComponents that contain or generate geometry for rendering or collision.
 * Subclasses: MeshComponent (GLTF, Instanced), CollisionShapeComponent, VoxelMeshComponent, TerrainMeshComponent.
 * Collision-related functionality will be moved here from SceneComponent (only primitives participate in collision).
 */
@AbstractEngineClass('PrimitiveComponent')
export abstract class PrimitiveComponent extends SceneComponent {

  static get DEFAULT_PHYSICS_OPTIONS(): ComponentPhysicsOptions {
    return {
      enabled: true,
      motionType: PhysicsMotionType.Static,
      density: 1,
      gravityScale: 1,
      generateCollisionEvents: true,
      collisionMeshType: CollisionMeshType.ConvexHull,
      enableCCD: false,
      collisionProfile: DefaultCollisionProfile.Default,
    };
  }

  private cachedWorldPosition: THREE.Vector3 = new THREE.Vector3();
  private cachedWorldQuat: THREE.Quaternion = new THREE.Quaternion();

  private tempPosition: THREE.Vector3 = new THREE.Vector3();
  private tempQuat: THREE.Quaternion = new THREE.Quaternion();

  @property()
  protected physicsOptions: ComponentPhysicsOptions = PrimitiveComponent.DEFAULT_PHYSICS_OPTIONS;

  @property({ description: 'Walkable contributes floor surfaces. Obstacle blocks paths but is never walkable. Exclude omits geometry from the bake.'})
  public navigationGeometryRole: NavigationRole = NavigationRole.Walkable;

  /** @deprecated Use navigationGeometryRole. Kept for scene serialization compatibility. */
  @property({ hidden: true, description: 'Legacy. Use navigationGeometryRole instead.' })
  public excludeFromNavigation: boolean = false;

  // Moved from SceneComponent: controls which transform components sync with physics
  protected physicsTransformUpdateFlags: PhysicsTransformUpdateFlags = {
    sendPosition: true,
    sendRotation: true,
    receivePosition: true,
    receiveRotation: true,
  };

  /**
   * Delegate called when this component collides with another physics-enabled component
   * Parameters: [this component, other component, collision event]
   */
  public readonly onCollideWith: Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void> = new Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void>();

  /**
   * Delegate called when this component overlaps with another physics-enabled component
   * Parameters: [this component, other component, collision event]
   */
  public readonly onOverlapWith: Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void> = new Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void>();

  /**
   * Delegate called when this component stops colliding with another physics-enabled component
   * Parameters: [this component, other component, collision event]
   */
  public readonly onStopCollidingWith: Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void> = new Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void>();

  /**
   * Delegate called when this component stops overlapping with another physics-enabled component
   * Parameters: [this component, other component, collision event]
   */
  public readonly onStopOverlappingWith: Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void> = new Delegate<[PrimitiveComponent, PrimitiveComponent, CollisionEvent], void>();



  public override initialize(options?: PrimitiveComponentOptions): void {
    super.initialize(options);
    if (options?.physicsOptions) {
      Object.assign(this.physicsOptions, options?.physicsOptions);
    }
    this.navigationGeometryRole = options?.navigationGeometryRole ?? this.navigationGeometryRole;
    this.excludeFromNavigation = options?.excludeFromNavigation ?? this.excludeFromNavigation;
    this.reconcileNavigationGeometryStateFromLegacy();
  }

  /**
   * Effective navmesh role for this component, honoring legacy excludeFromNavigation when present.
   */
  public getNavigationGeometryRole(): NavigationRole {
    if (this.excludeFromNavigation) {
      return NavigationRole.Exclude;
    }
    return this.navigationGeometryRole ?? NavigationRole.Walkable;
  }

  public override onEditorPropertyChanged(path: string, value: unknown, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);

    if (result.isOptions || path !== 'navigationGeometryRole') {
      return;
    }

    this.syncLegacyExcludeFromNavigation();
  }

  /** On load, legacy excludeFromNavigation wins over a stale navigationGeometryRole. */
  private reconcileNavigationGeometryStateFromLegacy(): void {
    if (this.excludeFromNavigation) {
      this.navigationGeometryRole = NavigationRole.Exclude;
      return;
    }

    this.syncLegacyExcludeFromNavigation();
  }

  private syncLegacyExcludeFromNavigation(): void {
    this.excludeFromNavigation = this.navigationGeometryRole === NavigationRole.Exclude;
  }

  public getPhysicsTransformUpdateFlags(): PhysicsTransformUpdateFlags {
    return this.physicsTransformUpdateFlags;
  }

  public override replacePhysicsOptions(newOptions: ComponentPhysicsOptions, propagateToChildren: boolean = false): this {
    super.replacePhysicsOptions(newOptions, propagateToChildren);
    this.doApplyPhysicsOptions(newOptions);
    return this;
  }

  public override overridePhysicsOptions(newOptions: ComponentPhysicsOptions, propagateToChildren: boolean = false): this {
    // NOTE: MUST NOT call replacePhysicsOptions({ ...this.physicsOptions, ...newOptions }) here as that will make the children inherit the parent's physicsOptions
    super.overridePhysicsOptions(newOptions, propagateToChildren);
    this.doApplyPhysicsOptions({ ...this.physicsOptions, ...newOptions });
    return this;
  }

  protected doApplyPhysicsOptions(newOptions: ComponentPhysicsOptions): void {
    if (JSON.stringify(newOptions) === JSON.stringify(this.physicsOptions)) {
      return;
    }
    this.physicsOptions = newOptions;
    const physicsEngine = this.getPhysicsEngine();
    if (this.motionType) {
      physicsEngine?.removeComponent(this);
    }
    this.motionType = physicsEngine?.addComponent(this) ?? null;
  }

  public isPhysicsEnabled(): boolean {
    return this.physicsOptions?.enabled ?? false;
  }

  protected refreshPhysics(): void {
    const actor = this.getActor();
    if (actor?.isPlaying() && this.isPhysicsEnabled()) {
      const physicsEngine = this.getPhysicsEngine();
      if (physicsEngine) {
        // Remove existing physics representation
        physicsEngine.removeComponent(this);

        // Create new physics representation based on the loaded model
        this.motionType = physicsEngine.addComponent(this) ?? null;
      }
    }
  }

  public setPhysicsTransformUpdateFlags(flags: PhysicsTransformUpdateFlags): void {
    this.physicsTransformUpdateFlags = flags;
  }

  public override beginPlay(): void {
    super.beginPlay();
    // makes `onBeginPlay` invoked before adding to physics, is it a problem?
    this.motionType = this.getPhysicsEngine()?.addComponent(this) ?? null;
  }

  public override endPlay(): void {
    super.endPlay();

    // makes `onEndPlay` invoked before removing from physics, is it a problem?
    this.motionType = null;
    this.getPhysicsEngine()?.removeComponent(this);
  }

  public getPhysicsOptions(): ComponentPhysicsOptions {
    return this.physicsOptions;
  }

  public isSimulatingPhysics(): boolean {
    return !!this.motionType;
  }

  public getMotionType(): PhysicsMotionType | null {
    return this.motionType;
  }

  public getPhysicsScalarParam(param: PhysicsScalarParam): number | null {
    return this.getPhysicsEngine()?.getScalarParam(this, param) ?? null;
  }

  public setPhysicsScalarParam(param: PhysicsScalarParam, value: number): boolean {
    return this.getPhysicsEngine()?.setScalarParam(this, param, value) ?? false;
  }

  public getPhysicsVectorParam(param: PhysicsVectorParam): VectorArray | null {
    return this.getPhysicsEngine()?.getVectorParam(this, param) ?? null;
  }

  public setPhysicsVectorParam(param: PhysicsVectorParam, value: VectorArray): boolean {
    return this.getPhysicsEngine()?.setVectorParam(this, param, value) ?? false;
  }

  public clearPhysicsVectorParam(param: PhysicsVectorParam): boolean {
    return this.getPhysicsEngine()?.clearVectorParam(this, param) ?? false;
  }

  protected shouldSyncToPhysics(): boolean {
    this.getWorldPosition(this.tempPosition);
    this.getWorldQuaternion(this.tempQuat);
    if (!this.tempPosition.equals(this.cachedWorldPosition) ||
        !this.tempQuat.equals(this.cachedWorldQuat)) {
      return true;
    }
    return false;
  }

  public syncToPhysics(physicsEngine: IPhysicsEngine): this {
    if (this.motionType) {
      this.updateMatrixWorld(true);
      if (this.shouldSyncToPhysics()) {
        physicsEngine.updateTransform(this);
      }
    }
    return this;
  }

  public syncFromPhysics(position: THREE.Vector3, quat?: THREE.Quaternion): this {
    if (this.physicsTransformUpdateFlags.receivePosition) {
      this.setWorldPosition(position);
    }
    if (this.physicsTransformUpdateFlags.receiveRotation && quat) {
      this.setWorldQuaternion(quat);
    }

    if (this.motionType) {
      this.updateMatrixWorld(true);
      this.getWorldPosition(this.cachedWorldPosition);
      this.getWorldQuaternion(this.cachedWorldQuat);
    }
    return this;
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_PrimitiveComponent';
  }
}
